import React, { useMemo, useCallback } from 'react';
import styled from '@emotion/styled';
import { IngestFile } from '@/redux/slices/slice_types';
import PolarisIcon from '../PolarisIcon';
import { Box, darken, Grid, Tooltip } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useAppSelector } from '@/redux/hooks';
import { selectStages, selectStatuses } from '@/redux/selectors/constantsSelectors';
import ConditionalTooltip from '../ConditionalTooltip/ConditionalTooltip';
import { previewEnabledTypes } from '@/components/IngestManagement/IngestManagementPreview';
import IngestAwaitingFileButton from './IngestAwaitingFileButton';
import ReadyToRegisterButton from '@/components/_common/ReadyToRegisterButton';

interface IngestManagementRowExpandProps {
  data: IngestFile;
  previewWidth: number;
}

const statusConfig = {
  completed: { icon: 'clipboard_done', color: 'success' },
  not_started: { icon: 'clipboard', color: 'default' },
  in_progress: { icon: 'clipboard_pending', color: 'warning' },
  failed: { icon: 'clipboard_failed', color: 'error' }
};

const IngestManagementRowExpand = React.memo(({ data, previewWidth }: IngestManagementRowExpandProps) => {
  const history = useHistory();
  const statuses = useAppSelector(selectStatuses);
  const stageConstants = useAppSelector(selectStages);
  const { stages, actions, auto_ingest } = data;
  if (!statuses || !stageConstants) return null;

  // set to completed if the last stage is complete
  const timelineCompleted = useMemo(() => stages?.[stages.length - 1]?.status === 'complete', [stages]);

  // Find the last index in stages that has a `name` that matches data.stage
  // If none match, find the last index that has a `status` of 'completed' or 'in_progress'
  const stepSelected = useMemo(
    () =>
      stages?.reduceRight((acc, stage, index) => {
        if (acc === -1 && (stage.name === data.stage || stage.status === 'complete' || stage.status === 'in_progress')) {
          return index;
        }
        return acc;
      }, -1),
    [stages, data.stage]
  );

  const handleReadyToIngestOnClick = useCallback(() => {
    history.push(`/ingest-management/${data.id}`);
  }, [history, data.id]);

  return (
    <Wrapper previewWidth={previewEnabledTypes.includes(data?.file_type) ? previewWidth : undefined}>
      {actions?.length > 0 && (
        <ActionTimeline>
          {actions?.map((action, index) => {
            const { icon, color } = statusConfig[action.status as keyof typeof statusConfig];
            return (
              <Action key={index}>
                <span>{action.display_name}</span>
                <ConditionalTooltip show={!!action.status_reason?.length} title={action.status_reason || ''} placement="top" arrow>
                  <ClipboardIcon icon={icon} height={22} color={color} />
                </ConditionalTooltip>
              </Action>
            );
          })}
        </ActionTimeline>
      )}
      <ProgressBar step={stepSelected} total={stages?.length} isComplete={+timelineCompleted} />
      <StatusTimeline>
        {stages?.map((stage, index) => (
          <TimelineItem key={index} noarrow={+(index === 0)}>
            {index !== 0 && <RightArrowIcon className="arrow" icon="right_arrow" height={20} width={20} />}
            {data.stage === 'awaiting_file' && index === stepSelected ? (
              <IngestAwaitingFileButton ingestRecord={data} />
            ) : stage.name === 'ready_for_ingest' && index === stepSelected && !auto_ingest ? (
              <div>
                <ReadyToRegisterButton fileType={data.file_type} color="warning" onClick={handleReadyToIngestOnClick} />
              </div>
            ) : (
              <Grid container alignItems="center" justifyContent="center" flexWrap="nowrap">
                <Status
                  future={+(stage.status !== 'complete' && stage.status !== 'in_progress' && index > stepSelected)}
                  error={+(stage.status === 'failed')}
                >
                  {stageConstants[stage.name] || stage.name}
                </Status>
                {stage.status_reason && (
                  <Box sx={{ color: 'error.main', height: 16, ml: 1 }}>
                    <Tooltip title={stage.status_reason}>
                      <PolarisIcon icon="error" height={16} />
                    </Tooltip>
                  </Box>
                )}
              </Grid>
            )}
          </TimelineItem>
        ))}
      </StatusTimeline>
    </Wrapper>
  );
});

export default IngestManagementRowExpand;

const Wrapper = styled.div<{ previewWidth?: number }>`
  height: 150px;
  width: ${({ previewWidth, theme }) => (previewWidth ? `calc(100% - ${previewWidth}px + ${theme.spacing(1.5)})` : '100%')};
  overflow-x: scroll;
  overflow-y: hidden;
  z-index: 10;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
`;

const ActionTimeline = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  column-gap: ${({ theme }) => theme.spacing(1)};
  margin: ${({ theme }) => theme.spacing(2, 0, 3, 2)};

  align-items: center;
  width: auto;
  white-space: nowrap;
`;

const Action = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: ${({ theme }) => theme.spacing(1)};

  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  background: ${({ theme }) => darken(theme.palette.background.default, 0.2)};
  padding: ${({ theme }) => theme.spacing(1)};
`;

const ClipboardIcon = styled(PolarisIcon)<{ color: string }>`
  color: ${({ theme, color }) => theme.palette[color as keyof typeof theme.palette].main};
  cursor: pointer;
`;

const StatusTimeline = styled.div`
  display: flex;
  flex-direction: row;
`;

const TimelineItem = styled.div<{ noarrow: number }>`
  min-width: ${({ noarrow }) => (noarrow ? '200px' : '220px')};
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;

  > div {
    width: 100%;
    text-align: center;
  }
`;

const RightArrowIcon = styled(PolarisIcon)`
  margin: ${({ theme }) => theme.spacing(0, 1)};
  min-width: 18px;
  min-height: 18px;
  color: ${({ theme }) => theme.palette.primary.main};
`;

const ProgressBar = styled.div<{ total: number; step: number; isComplete: number }>`
  height: 10px;
  width: ${({ total }) => `${total * 220}px`};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  background: ${({ theme, isComplete }) => (isComplete ? theme.palette.success.main : darken(theme.palette.background.paper, 0.1))};

  &::before {
    content: '';
    display: block;
    height: 100%;
    width: ${({ total, step }) => {
      const statusWidth = 200;
      const arrowWidth = 20;
      const totalWidth = total * statusWidth + (total - 1) * arrowWidth;
      const progressWidth = statusWidth / 2 + step * (statusWidth + arrowWidth);
      return `${(progressWidth / totalWidth) * 100}%`;
    }};
    border-radius: ${({ theme }) => theme.shape.borderRadius}px;
    background: ${({ theme, isComplete }) => (isComplete ? theme.palette.success.main : `linear-gradient(90deg, #3887f4 0%, #5f1599 100%)`)};
  }
`;

const Status = styled.div<{ future: number; error: number }>`
  text-transform: uppercase;
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  opacity: ${({ future }) => (future ? 0.3 : 1)};
  ${({ theme, error }) => error && `color: ${theme.palette.error.main}`};
`;
